import PropTypes from 'prop-types';
import React from 'react';
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaTwitter,
  FaYoutube,
} from 'react-icons/fa';

import { propTypesActiveDocMeta } from '../../../constants/Proptypes';
import { propTypesFooter } from './Footer.proptypes';

import { defaultLanguage } from '../../../../../config/prismic';

import LanguageSelect from '../../../../ui/Molecules/LanguageSelect';
import { Color } from '../../../constants/Colors';
import { DocumentType } from '../../../constants/Enums';
import * as S from './Footer.styles';

const socialIcons = {
  Facebook: <FaFacebookF />,
  Twitter: <FaTwitter />,
  Instagram: <FaInstagram />,
  LinkedIn: <FaLinkedinIn />,
  Youtube: <FaYoutube />,
};

const clickObfuscateLink = ev => {
  const attribute = ev.currentTarget.getAttribute('data-variables');
  if (typeof window !== 'undefined') {
    document.location.href = decodeURIComponent(window.atob(attribute));
  }
};

const SectionLink = ({ label, link, obfuscateLabel, isHomepage }) => {
  if (!label || !link) {
    return null;
  }

  const { text } = label || '';
  const { url, target } = link || '';

  const toObfuscate =
    obfuscateLabel === 'all_pages_except_homepage' ||
    obfuscateLabel === 'all_pages';

  const obfuscateLink =
    obfuscateLabel === 'all_pages_except_homepage'
      ? toObfuscate && !isHomepage
      : toObfuscate;

  const SectionLinkWrapper = obfuscateLink
    ? S.SectionLinkObfuscate
    : S.SectionLink;

  if (!text || !url) {
    return null;
  }

  let args = { href: url, alt: text, target };
  args = obfuscateLink
    ? {
        'data-variables': btoa(url),
        onClick: clickObfuscateLink,
      }
    : args;

  // Note: last index {label: undefined, link: function()} To Investigate
  return (
    <S.LinkContainer>
      <SectionLinkWrapper {...args}>{text}</SectionLinkWrapper>
    </S.LinkContainer>
  );
};

const Column = ({
  column,
  isHomepage,
  isWebsite,
  openCookieBanner,
  cookieLabel,
  index,
}) => {
  const { text } = column || '';

  if (!text) {
    return null;
  }

  const isCookieButtonDisplayed =
    (isWebsite && index === 4) || (!isWebsite && index === 3);

  return (
    <S.Section>
      <S.ColumnTitle>{text}</S.ColumnTitle>
      <S.UnorderedLinks>
        {Object.values(column).map(
          ({ label, link, obfuscate: obfuscateLabel }, linkIndex) => (
            <SectionLink
              key={`item-${linkIndex}`}
              label={label}
              link={link}
              obfuscateLabel={obfuscateLabel}
              isHomepage={isHomepage}
            />
          )
        )}
        {isCookieButtonDisplayed && (
          <S.ButtonCookie onClick={() => openCookieBanner()}>
            {cookieLabel}
          </S.ButtonCookie>
        )}
      </S.UnorderedLinks>
    </S.Section>
  );
};

// Render the social network logos.
const SocialNetwork = ({ socialNetwork, isHomepage }) => {
  const {
    social_link: { url },
    social_name: socialName,
    obfuscate: obfuscateLabel,
  } = socialNetwork || '';

  if (!url || !socialName) {
    return null;
  }

  const toObfuscate =
    obfuscateLabel === 'all_pages_except_homepage' ||
    obfuscateLabel === 'all_pages';

  const obfuscateLink =
    obfuscateLabel === 'all_pages_except_homepage'
      ? toObfuscate && !isHomepage
      : toObfuscate;

  const SocialLinkWrapper = obfuscateLink
    ? S.SocialLinkObfuscate
    : S.SocialLink;

  const args = obfuscateLink
    ? {
        'data-variables': btoa(url),
        onClick: clickObfuscateLink,
      }
    : { href: url, target: '_blank', rel: 'noopener noreferrer' };

  return (
    <SocialLinkWrapper {...args}>{socialIcons[socialName]}</SocialLinkWrapper>
  );
};

const RootLogo = ({ lang }) => {
  const homepageUrl = `https://www.wooclap.com/${
    lang === defaultLanguage ? '' : `${lang.slice(0, 2)}/`
  }`;

  return (
    <S.RootLog href={homepageUrl}>
      <S.Logo
        alt={'Wooclap blue logo'}
        loading='lazy'
        src={'https://www.wooclap.com/images/wooclap-logo-blue.svg'}
      />
    </S.RootLog>
  );
};

export const Footer = ({ footer, activeDocMeta, openCookieBanner }) => {
  if (!footer || !activeDocMeta) {
    return null;
  }

  const { data: document } = footer || {};

  const {
    description: { text: slogan },
    social_networks: networks,
    open_cookie_label: cookieLabel,
    language_aria_label: languageAriaLabel,
  } = document ?? {};

  const { lang, type, alternateLanguages } = activeDocMeta ?? {};
  const isWebsite =
    type === DocumentType.PAGE ||
    type === DocumentType.CONTACT ||
    type === DocumentType.HOMEPAGEWEB ||
    type === DocumentType.NOTFOUND;

  const isHomepage =
    type === DocumentType.HOMEPAGE || type === DocumentType.HOMEPAGEWEB;

  const getColumns = () => {
    const prefixColumn = isWebsite ? 'website' : 'blog';

    const numberOfItems = ['first', 'second', 'third', 'fourth'];
    if (isWebsite) {
      numberOfItems.push('fifth');
    }
    const _getContents = numberOfItems.map(
      item => document[`${prefixColumn}_${item}_column_content`] || {}
    );

    const _getTitles = numberOfItems.map(
      item => document[`${prefixColumn}_${item}_column_name`] || {}
    );

    const columns = _getTitles.map((item, index) =>
      Object.assign({}, _getContents[index], item)
    );

    return columns;
  };

  return (
    <S.Footer>
      <svg height='1em' width='1em'>
        <linearGradient id='blue-gradient' x1='0' x2='0' y1='0' y2='100%'>
          <stop stopColor={Color.BLUE_DODGER} />
          <stop offset='1' stopColor={Color.BLUE_RIBBON} />
        </linearGradient>
      </svg>
      <S.FooterContainer>
        <S.FooterWrapper>
          <S.Section flexGrow='2'>
            <S.LogoContainer>
              {lang && <RootLogo lang={lang} />}
            </S.LogoContainer>
            {slogan && <S.Slogan>{slogan}</S.Slogan>}
            {networks.length && (
              <S.SocialNav>
                {networks.map((socialNetwork, index) => (
                  <SocialNetwork
                    key={`${socialNetwork}-${index}`}
                    socialNetwork={socialNetwork}
                    isHomepage={isHomepage}
                  />
                ))}
              </S.SocialNav>
            )}
            <LanguageSelect
              alternateLanguages={alternateLanguages}
              ariaLabel={languageAriaLabel || 'Select language'}
              lang={lang}
            />
          </S.Section>
          {getColumns().map((column, index) => (
            <Column
              isWebsite={isWebsite}
              index={index}
              key={`${column}-${index}`}
              column={column}
              isHomepage={isHomepage}
              openCookieBanner={openCookieBanner}
              cookieLabel={cookieLabel}
            />
          ))}
        </S.FooterWrapper>
      </S.FooterContainer>
    </S.Footer>
  );
};

Footer.propTypes = {
  /**
   * Language data from the current Prismic document.
   */
  activeDocMeta: PropTypes.shape(propTypesActiveDocMeta),
  /**
   * Footer document.
   */
  footer: PropTypes.shape(propTypesFooter),
  /**
   * Function opening the cookie modal.
   */
  openCookieBanner: PropTypes.func,
};
